import TableComponent from "../ui/Table"
import { urls } from "../config/variables";
import { useState, useEffect, useContext, useMemo } from "react"
import useAxios from "../../hooks/useAxios"
import AuthContext from "../../contexts/auth-context";
import AppContext from "../../contexts/app-context";

import { Box, TextField, Stack, Pagination, Tabs, Tab, Typography } from "@mui/material";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import { validateRefills } from "../../Utils/validateHttpResponse";
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import DangerousIcon from '@mui/icons-material/Dangerous';
import InfoIcon from '@mui/icons-material/Info';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';

const PendingRefillsTable = () => {
    const authCtx = useContext(AuthContext);
    const appCtx = useContext(AppContext);
    const globalFilterQuery = appCtx?.globalFilterQuery || '';
    const token = authCtx.token;
    const { operation } = useAxios();
    const [tabValue, setTabValue] = useState('Recently Refilled');

    const tableDataUrl = {
        "Overdue Refills": urls.PENDING_REFILLS,
        "Recently Refilled": urls.CURRENT_REFILLS,
        "Upcoming Refills": urls.UPCOMING_REFILLS,
    }

    const [tableData, setTableData] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [tableDataError, setTableDataError] = useState(undefined);

    const GetTableData = () => {
        setLoadingData(true)
        setTableDataError(undefined)
        const globalParams = new URLSearchParams(globalFilterQuery);
        const foundFromDate  = globalParams.get('from_date');
        const foundToDate  = globalParams.get('to_date');
        const tableParams =  new URLSearchParams();

        if(foundFromDate) {
          tableParams.set("from_date", foundFromDate);
        }
        if(foundToDate) {
          tableParams.set("to_date", foundToDate);
        }
        operation(
            {
                method: 'GET',
                url: tableDataUrl[tabValue] + '?' + tableParams,
                headers: { Authorization: `token ${token}` },
                data: {},
            },
            handleSetTableData,
            handleSetTableDataError
        );
    }

    const handleSetTableData = (response) => {
        setLoadingData(false)
        if(validateRefills(response)) {
            setTableData(response)
        } else {
            setTableDataError('validation response error.')
        }
    }

    const handleSetTableDataError = (error) => {
        setLoadingData(false)
        setTableDataError(error)
    }

    useEffect(() => {
        GetTableData()
        // eslint-disable-next-line
    }, [tabValue])

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;
    const handleChangeCurrentPage = (e, v) => setCurrentPage(v);

    // Search + Sorting
    const [searchValue, setSearchValue] = useState("");
    const [sortBy, setSortBy] = useState("next_refill");
    const [sortDirection, setSortDirection] = useState("asc");

    const handleSort = (columnKey) => {
        if (sortBy === columnKey) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(columnKey);
            setSortDirection("asc");
        }
    };

    // Filter + Sort
    const filteredData = useMemo(() => {
        let filtered = tableData.filter(item =>
            Object.values(item)
                .some(val => val?.toString().toLowerCase().includes(searchValue?.toLowerCase()))
        );

        filtered.sort((a, b) => {
            const aVal = a[sortBy];
            const bVal = b[sortBy];

            if (!isNaN(aVal) && !isNaN(bVal)) {
                return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
            } else if (aVal && bVal) {
                return sortDirection === "asc"
                    ? aVal.toString().localeCompare(bVal.toString())
                    : bVal.toString().localeCompare(aVal.toString());
            }

            return 0;
        });

        return filtered;
    }, [tableData, searchValue, sortBy, sortDirection]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    const tableHeaders = {
        "Overdue Refills": [
            { label: "Patient Name", key: "patient_name" },
            { label: "Product Brand Name", key: "product_brand_name" },
            { label: "Refill Cost", key: "refill_cost" },
            { label: "Last Refill", key: "last_refill" },
            { label: "Expected Refill", key: "expected_refill" },
            { label: "Days Overdue", key: "days_overdue" }
        ],
        "Recently Refilled": [
            { label: "Patient Name", key: "patient_name" },
            { label: "Product Brand Name", key: "product_brand_name" },
            { label: "Refill Cost", key: "refill_cost" },
            { label: "Refill Date", key: "refill_date" },
            { label: "Next Refill", key: "next_refill" },

        ],
        "Upcoming Refills": [
            { label: "Patient Name", key: "patient_name" },
            { label: "Product Brand Name", key: "product_brand_name" },
            { label: "Refill Cost", key: "refill_cost" },
            { label: "Last Refill Date", key: "last_refill_date" },
            { label: "Next Refill Date", key: "next_refill_date" },
            { label: "Days Til Next Refill", key: "days_til_next_refill" },

        ]
    };

    const titles = {
        "Overdue Refills": 'Medications past refill date requiring patient action.',
        "Recently Refilled": 'Medications refilled within the past period.',
        "Upcoming Refills": 'Medications due for refill in the future.',
    }

    const handleChangeTab = (e, newVal) => {
        setLoadingData(true)
        setTabValue(newVal)
        setCurrentPage(1)
        setSearchValue('')
        setSortDirection('asc')
        setSortBy(newVal === 'Overdue Refills' ? 'days_overdue' : newVal === 'Recently Refilled' ? 'next_refill' : 'days_til_next_refill')
    }

    return (
    <Box sx={{ position: 'relative', height: '680px' }}>
        {/* Search and Tabs */}
        <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 2, alignItems: 'center', justifyContent: 'space-between' }}
        >
            <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchValue}
                onChange={(e) => {
                    setSearchValue(e.target.value);
                    setCurrentPage(1);
                    }}
                    InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                    }}
                />

            <Tabs
                value={tabValue}
                onChange={handleChangeTab}
                sx={{ mb: 2 }}
                centered
            >
                <Tab label="Recently Refilled" value="Recently Refilled" />
                <Tab label="Upcoming Refills" value="Upcoming Refills" />
                <Tab label="Overdue Refills" value="Overdue Refills" />
            </Tabs>
        </Stack>

        {/* Conditional Rendering */}
        {(!tableDataError && tableData.length === 0 && !loadingData) ? (
            <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                No data available.
            </Alert>
        ) : tableDataError ? (
            <Alert icon={<DangerousIcon fontSize="inherit" />} severity="error">
                Something went wrong.
            </Alert>
        ) : loadingData ? (
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        ) : (
            <>
                <Box sx={{height: '570px'}}>
                    <Typography sx={{color: '#86878C', fontWeight: '500', marginBottom: '10px'}}>{titles[tabValue]}</Typography>
                    {/* Table */}
                <TableComponent
                    tableHead={tableHeaders[tabValue].map((h) => (
                        <Box
                            key={h.key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                            }}
                            onClick={() => handleSort(h.key)}
                        >
                            {h.label}
                            {sortBy === h.key && (
                                sortDirection === 'asc' ? (
                                    <ArrowDropUp fontSize="small" />
                                ) : (
                                    <ArrowDropDown fontSize="small" />
                                )
                            )}
                        </Box>
                    ))}
                    tableBody={currentRows}
                    bodyCells={tableHeaders[tabValue].map((h) => h.key)}
                    bodyCellsCustomColor={{ days_overdue: '#FF0000' }}
                    bodyCellsCustomSymbol={{ refill_cost: '$' }}
                        customStyle={{
                            tableContainer: {
                        maxHeight: '555px',
                        overflowY: "auto"
                      },
                            headerTableCell: (cell) => {
                                return ({
                                textAlign: 'center',
                                border: '1px solid #d9d9d9',
                                fontSize: '18px',
                                fontWeight: '500',
                                color: '#000000c7',
                                backgroundColor: '#F5F5F5',
                                padding: '10px 0 5px',
                                width:
                                cell.key === 'patient_name' ? '25%' :
                                cell.key === 'product_brand_name' ? '25%' :
                                '12.5%',
                            })
                            },
                            row: {
                                transition: '0.2s',
                            border: '1px solid #d9d9d9',
                                backgroundColor: '#FCFEFE',
                                '&:hover': {
                                backgroundColor: '#f9f9f9',
                                },
                            },
                            tableCell: {
                                border: '1px solid #d9d9d9',
                                textAlign: 'center',
                                padding: '14px',
                                fontWeight: '500',
                            },
                            }}
                    />
                </Box>

                {/* Pagination */}
                {tableData.length > 10 && <Box
                    sx={{
                        position: 'absolute',
                        left: '50%',
                        bottom: '-15px',
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    <Stack spacing={2}>
                        <Pagination
                            page={currentPage}
                            count={totalPages}
                            variant="outlined"
                            color="primary"
                            size="medium"
                            onChange={handleChangeCurrentPage}
                        />
                    </Stack>
                </Box>}
            </>
        )}
    </Box>
  )
}

export default PendingRefillsTable;
